import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { useAuth } from '@/shared/auth/store';

/** Flags we hang on a request config to stop the interceptor recursing. */
type TracedConfig = InternalAxiosRequestConfig & {
  /** The session probe below — must never trigger another probe. */
  _sessionProbe?: boolean;
  /** A request already replayed once after refreshing the CSRF token. */
  _csrfRetried?: boolean;
};

/**
 * Single axios instance for all API calls.
 * - Sends Sanctum session cookies (withCredentials).
 * - CSRF cookie fetched lazily via ensureCsrf().
 * - Attaches X-Tenant-Id if the user has picked a tenant.
 */
export const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '',
  withCredentials: true,
  headers: {
    Accept: 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
});

let csrfFetched = false;

export async function ensureCsrf(): Promise<void> {
  if (csrfFetched) return;
  await api.get('/sanctum/csrf-cookie');
  csrfFetched = true;
}

api.interceptors.request.use((cfg) => {
  const tenantId = localStorage.getItem('current_tenant_id');
  if (tenantId && cfg.headers) {
    cfg.headers['X-Tenant-Id'] = tenantId;
  }
  return cfg;
});

/**
 * Is the session genuinely gone, or did one request just lose a race?
 *
 * A single 401 is not proof. It can come from a background poller, an offline
 * replay that started before sign-in, or an endpoint that guards itself — and
 * signing someone out mid-shift because of one stray response is far worse
 * than spending a round-trip to check. Concurrent callers share one probe.
 */
let sessionProbe: Promise<boolean> | null = null;

function sessionIsDead(): Promise<boolean> {
  sessionProbe ??= api
    .get('/api/v1/me', { _sessionProbe: true } as TracedConfig)
    .then(() => false)
    .catch((e: AxiosError) => e.response?.status === 401)
    .finally(() => {
      sessionProbe = null;
    });

  return sessionProbe;
}

api.interceptors.response.use(
  (r) => r,
  async (err: AxiosError) => {
    const status = err.response?.status;
    const cfg = err.config as TracedConfig | undefined;

    // 419 means the CSRF token is stale — which is exactly what happens to any
    // request prepared before a login, because logging in regenerates the
    // session and with it the token. Refresh and replay once rather than
    // failing an action the user is watching.
    if (status === 419 && cfg && !cfg._csrfRetried) {
      cfg._csrfRetried = true;
      csrfFetched = false;
      try {
        await ensureCsrf();
        return await api.request(cfg);
      } catch {
        // Fall through and reject with the original error.
      }
    }

    if (status === 401 && !cfg?._sessionProbe && (await sessionIsDead())) {
      // Persisted zustand state (user, tenants, roles…) stays in localStorage
      // across a dead session. Without clearing it, the redirect below lands on
      // /login where GuestGuard still sees a `user` and bounces straight back
      // to /dashboard — /me 401s again — infinite loop. Clear first, then go.
      useAuth.getState().clear();

      const path = window.location.pathname;
      if (path !== '/login' && !path.startsWith('/forgot') && !path.startsWith('/register')) {
        // Full reload so query cache, session-scoped state and CSRF flag reset.
        window.location.href = '/login';
      }
    }

    return Promise.reject(err);
  },
);

export type ApiError = AxiosError<{
  message?: string;
  errors?: Record<string, string[]>;
}>;
